import React, { useContext, useEffect, useRef } from 'react';
import { Button, View } from '@aws-amplify/ui-react';
import { MdClose } from 'react-icons/md';
import { ModalContext } from '../../context/ModalContext';
import './Modal.css';

const Modal = () => {
  const { modalContent, isModalOpen, closeModal } = useContext(ModalContext);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
      previousFocusRef.current = document.activeElement as HTMLElement;
      closeButtonRef.current?.focus();
    } else {
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, closeModal]);

  useEffect(() => {
    if (!isModalOpen) return;
    const dialog = closeButtonRef.current?.closest('.modal-dialog') as HTMLElement | null;
    if (!dialog) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          last.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  if (!isModalOpen) return null;

  return (
    <>
      <View
        role="dialog"
        aria-modal="true"
        className="modal-dialog"
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        maxWidth="95vw"
        maxHeight="95vh"
        borderRadius="medium"
        overflow="auto"
      >
        <Button
          ref={closeButtonRef}
          variation="link"
          size="small"
          onClick={closeModal}
          aria-label="Close dialog"
          className="modal-dialog__close"
          position="absolute"
          top="small"
          right="small"
        >
          <MdClose size={20} />
        </Button>
        {modalContent}
      </View>

      <View
        aria-hidden="true"
        className="modal-overlay"
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        backgroundColor="overlay.80"
        onClick={closeModal}
      />
    </>
  );
};

export default Modal;
